import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import HttpBackend from "i18next-http-backend";
import { useAuth } from "@/services/auth/AuthContext";
import { useRef } from "react";
import { translationDefaults } from "@/constants";
import { buildApiUrl } from "@/services/api/baseUrl";

interface TranslationDefaults {
  component: string;
  version: string;
  language: string;
}

const fetchAndApplyOverrides = async (
  token: string,
  tenantId: string,
  language: string
) => {
  try {
    const response = await fetch(
      buildApiUrl(`/api/v1/translations/overrides/${language}?namespace=webapp`),
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-Id": tenantId,
          "Content-Type": "application/json"
        }
      }
    );
    if (!response.ok) return;

    const overrides = await response.json();
    if (overrides && Object.keys(overrides).length > 0) {
      i18n.addResourceBundle(language, "translation", overrides, true, true);
    }
  } catch {
    // Overrides are non-critical — base translations still work
  }
};

const useI18n = (localOnly: boolean = false) => {
  const { language }: TranslationDefaults = translationDefaults;
  const { token, tenantId } = !localOnly
    ? useAuth()
    : { token: null, tenantId: null };
  const initialized = !localOnly ? useRef(false) : { current: false };

  const cacheBuster = `v=${new Date().getTime()}`;
  new URL(
    `/public/locales/en-US/translation.json?${cacheBuster}`,
    import.meta.url
  ).href;

  if ((token && !initialized.current) || localOnly) {
    i18n
      .use(HttpBackend)
      .use(initReactI18next)
      .init({
        fallbackLng: language,
        debug: false,
        ns: ["translation"],
        defaultNS: "translation",
        react: {
          useSuspense: false
        },
        load: "currentOnly",
        interpolation: {
          escapeValue: false
        },
        backend: {
          backends: [],
          loadPath: `/locales/{{lng}}/translation.json?${cacheBuster}`,
          backendOptions: [
            { expirationTime: 12 * 60 * 60 * 1000 } // 12 hours
          ]
        }
      });

    if (token && !localOnly) {
      const applyOverrides = () => {
        const resolvedLanguage = i18n.language || language;
        fetchAndApplyOverrides(token, tenantId!, resolvedLanguage);
      };

      if (i18n.isInitialized) {
        applyOverrides();
      } else {
        const onLoaded = () => {
          i18n.off("loaded", onLoaded);
          applyOverrides();
        };
        i18n.on("loaded", onLoaded);
      }
    }

    initialized.current = true;
  }
  return i18n;
};

export default useI18n;
